import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getDatabase, ref, get } from 'firebase/database';
import hireMeLogo from '../../assets/hireme-logo.png';
import { FaBars, FaTimes } from 'react-icons/fa';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { toast } from 'react-toastify';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSignUpDropdownOpen, setIsSignUpDropdownOpen] = useState(false);
  const [isSignInDropdownOpen, setIsSignInDropdownOpen] = useState(false);
  const [isMobileSignInOpen, setIsMobileSignInOpen] = useState(false);
  const [isMobileSignUpOpen, setIsMobileSignUpOpen] = useState(false);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const [isMobileBrowseOpen, setIsMobileBrowseOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  
  const auth = getAuth();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  
  const signUpDropdownRef = useRef(null);
  const signInDropdownRef = useRef(null);
  const mobileSignInRef = useRef(null);
  const mobileSignUpRef = useRef(null);
  const browseDropdownRef = useRef(null);
  const resourcesDropdownRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    let retryTimeout = null;

    const checkUserType = async (attempt = 1) => {
      if (user) {
        try {
          const db = getDatabase();

          const [candidateSnapshot, employerSnapshot, adminSnapshot] = await Promise.all([
            get(ref(db, `candidates/${user.uid}`)),
            get(ref(db, `employers/${user.uid}`)),
            get(ref(db, `admins/${user.uid}`))
          ]);

          if (!isMounted) return;

          if (adminSnapshot.exists()) {
            setUserType('admin');
            setUserLoaded(true);
            return;
          }

          if (candidateSnapshot.exists() && employerSnapshot.exists()) {
            console.error('User exists in both collections');
            setUserType(null);
            toast.error('Account error detected. Please contact support.');
            setUserLoaded(true);
            return;
          }

          if (candidateSnapshot.exists()) {
            setUserType('candidate');
            setUserLoaded(true);
          } else if (employerSnapshot.exists()) {
            setUserType('employer');
            setUserLoaded(true);
          } else {
            // User authenticated but not in any collection
            if (attempt < 3) {
              // Brief retry for fresh registrations where DB write may lag
              retryTimeout = setTimeout(() => {
                if (isMounted) checkUserType(attempt + 1);
              }, 1000 * attempt);
            } else {
              // Stale/orphaned auth session — sign out silently to clear it
              console.log('No profile data found, clearing stale auth session');
              await signOut(auth);
            }
          }
        } catch (error) {
          console.error('Error checking user type:', error);
          if (isMounted) {
            setUserType(null);
            toast.error('Error verifying account type. Please try again later.');
            setUserLoaded(true);
          }
        }
      } else {
        if (isMounted) {
          setUserType(null);
          setUserLoaded(true);
        }
      }
    };

    if (!loading) {
      checkUserType();
    }

    return () => {
      isMounted = false;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [user, loading, auth]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUserType(null);
      navigate('/');
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out. Please try again.');
    }
  };

  const getConsoleLink = () => {
    if (userType === 'candidate') {
      return '/candidate/profile';
    } else if (userType === 'employer') {
      return '/employer/profile';
    } else if (userType === 'admin') {
      return '/admin/dashboard';
    }
    return '/';
  };

  const isInConsole = () => {
    const path = location.pathname;
    return (userType === 'candidate' && path.includes('/candidate/')) || 
           (userType === 'employer' && path.includes('/employer/')) ||
           (userType === 'admin' && path.includes('/admin/'));
  };

  // Function to determine if pricing should be shown
  const shouldShowPricing = () => {
    return !user || userType !== 'candidate';
  };

  // Handle All Candidates click - show preview if not an employer
  const handleAllCandidatesClick = (e) => {
    if (userType === 'employer') {
      // Employer is signed in, navigate to full candidates page
      navigate('/employer/resumes');
    } else {
      // Not signed in or not an employer - show preview page
      navigate('/all-candidates');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsMobileSignInOpen(false);
    setIsMobileSignUpOpen(false);
    setIsMobileBrowseOpen(false);
    setIsMobileResourcesOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (signUpDropdownRef.current && !signUpDropdownRef.current.contains(event.target)) {
        setIsSignUpDropdownOpen(false);
      }
      if (signInDropdownRef.current && !signInDropdownRef.current.contains(event.target)) {
        setIsSignInDropdownOpen(false);
      }
      if (mobileSignInRef.current && !mobileSignInRef.current.contains(event.target)) {
        setIsMobileSignInOpen(false);
      }
      if (mobileSignUpRef.current && !mobileSignUpRef.current.contains(event.target)) {
        setIsMobileSignUpOpen(false);
      }
      if (browseDropdownRef.current && !browseDropdownRef.current.contains(event.target)) {
        setIsBrowseOpen(false);
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Authentication buttons based on user state
  const AuthButtons = () => {
    if (loading || !userLoaded) {
      return <div className="w-8 h-8 border-t-2 border-blue-950 rounded-full animate-spin"></div>;
    }

    if (user && userType) {
      return (
        <div className="flex items-center space-x-4">
          {!isInConsole() && userType !== 'admin' ? (
            <Link
              to={getConsoleLink()}
              className="px-4 py-2 text-blue-950 hover:text-[#cddd3a] transition-colors"
            >
              {userType === 'candidate' ? 'My Profile' : 'Employer Dashboard'}
            </Link>
          ) : isInConsole() && userType !== 'admin' ? (
            <Link
              to="/"
              className="px-4 py-2 text-blue-950 hover:text-[#cddd3a] transition-colors"
            >
              Main Website
            </Link>
          ) : null}
          <button
            onClick={handleSignOut}
            className="px-4 py-2 text-white bg-blue-950 rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
          >
            Sign Out
          </button>
        </div>
      );
    }

    return (
      <>
        {/* Sign In Dropdown */}
        <div className="relative" ref={signInDropdownRef}>
          <button
            onClick={() => {
              setIsSignInDropdownOpen(!isSignInDropdownOpen);
              setIsSignUpDropdownOpen(false);
            }}
            className="px-4 py-2 text-blue-950 hover:text-[#cddd3a] transition-colors flex items-center"
          >
            Sign In
            {isSignInDropdownOpen ? 
              <IoMdArrowDropup className="ml-1" size={20} /> : 
              <IoMdArrowDropdown className="ml-1" size={20} />
            }
          </button>
          
          {isSignInDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
              <Link
                to="/candidate-sign-in"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#cddd3a]"
                onClick={() => setIsSignInDropdownOpen(false)}
              >
                Sign in as Candidate
              </Link>
              <Link
                to="/employer-sign-in"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#cddd3a]"
                onClick={() => setIsSignInDropdownOpen(false)}
              >
                Sign in as Employer
              </Link>
            </div>
          )}
        </div>

        {/* Sign Up Dropdown */}
        <div className="relative" ref={signUpDropdownRef}>
          <button
            onClick={() => {
              setIsSignUpDropdownOpen(!isSignUpDropdownOpen);
              setIsSignInDropdownOpen(false);
            }}
            className="px-4 py-2 bg-blue-950 text-white rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors flex items-center"
          >
            Sign Up
            {isSignUpDropdownOpen ? 
              <IoMdArrowDropup className="ml-1" size={20} /> : 
              <IoMdArrowDropdown className="ml-1" size={20} />
            }
          </button>
          
          {isSignUpDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
              <Link
                to="/candidate-registration"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#cddd3a]"
                onClick={() => setIsSignUpDropdownOpen(false)}
              >
                Register as Candidate
              </Link>
              <Link
                to="/employer-registration"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#cddd3a]"
                onClick={() => setIsSignUpDropdownOpen(false)}
              >
                Register as Employer
              </Link>
            </div>
          )}
        </div>
      </>
    );
  };

  // Mobile Auth Buttons - similar changes as desktop
  const MobileAuthButtons = () => {
    if (loading || !userLoaded) {
      return <div className="w-8 h-8 border-t-2 border-blue-950 rounded-full animate-spin"></div>;
    }

    if (user && userType) {
      return (
        <div className="flex flex-col space-y-2">
          {!isInConsole() && userType !== 'admin' ? (
            <Link
              to={getConsoleLink()}
              className="w-full py-2 text-blue-950 hover:text-[#cddd3a] transition-colors text-center"
              onClick={toggleMenu}
            >
              {userType === 'candidate' ? 'My Profile' : 'Employer Dashboard'}
            </Link>
          ) : isInConsole() && userType !== 'admin' ? (
            <Link
              to="/"
              className="w-full py-2 text-blue-950 hover:text-[#cddd3a] transition-colors text-center"
              onClick={toggleMenu}
            >
              Main Website
            </Link>
          ) : null}
          <button
            onClick={() => {
              handleSignOut();
              toggleMenu();
            }}
            className="w-full py-2 text-blue-950 hover:text-[#cddd3a] transition-colors text-center"
          >
            Sign Out
          </button>
        </div>
      );
    }

    return (
      <>
        {/* Mobile Sign In Dropdown */}
        <div ref={mobileSignInRef} className="text-center">
          <button
            onClick={() => {
              setIsMobileSignInOpen(!isMobileSignInOpen);
              setIsMobileSignUpOpen(false);
            }}
            className="w-full flex justify-center items-center py-2 text-blue-950 hover:text-[#cddd3a] transition-colors space-x-2"
          >
            <span>Sign In</span>
            {isMobileSignInOpen ? 
              <IoMdArrowDropup size={20} /> : 
              <IoMdArrowDropdown size={20} />
            }
          </button>
          {isMobileSignInOpen && (
            <div className="space-y-2 bg-gray-50 py-2">
              <Link
                to="/candidate-sign-in"
                className="block py-2 text-gray-700 hover:text-[#cddd3a] transition-colors text-center"
                onClick={toggleMenu}
              >
                Sign in as Candidate
              </Link>
              <Link
                to="/employer-sign-in"
                className="block py-2 text-gray-700 hover:text-[#cddd3a] transition-colors text-center"
                onClick={toggleMenu}
              >
                Sign in as Employer
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Sign Up Dropdown */}
        <div ref={mobileSignUpRef} className="text-center">
          <button
            onClick={() => {
              setIsMobileSignUpOpen(!isMobileSignUpOpen);
              setIsMobileSignInOpen(false);
            }}
            className="w-full flex justify-center items-center py-2 text-blue-950 hover:text-[#cddd3a] transition-colors space-x-2"
          >
            <span>Sign Up</span>
            {isMobileSignUpOpen ? 
              <IoMdArrowDropup size={20} /> : 
              <IoMdArrowDropdown size={20} />
            }
          </button>
          {isMobileSignUpOpen && (
            <div className="space-y-2 bg-gray-50 py-2">
              <Link
                to="/candidate-registration"
                className="block py-2 text-gray-700 hover:text-[#cddd3a] transition-colors text-center"
                onClick={toggleMenu}
              >
                Register as Candidate
              </Link>
              <Link
                to="/employer-registration"
                className="block py-2 text-gray-700 hover:text-[#cddd3a] transition-colors text-center"
                onClick={toggleMenu}
              >
                Register as Employer
              </Link>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <nav className="bg-white shadow-md fixed w-full z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={hireMeLogo} 
              alt="HireMe Logo" 
              className="h-8 sm:h-10 md:h-12 w-auto" 
            />
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-blue-950"
            onClick={toggleMenu}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Browse Jobs mega-menu */}
            <div ref={browseDropdownRef}>
              <button
                onClick={() => setIsBrowseOpen(prev => !prev)}
                className="text-gray-700 hover:text-[#cddd3a] transition-colors flex items-center gap-1"
              >
                Browse Jobs
                {isBrowseOpen ? <IoMdArrowDropup size={20} /> : <IoMdArrowDropdown size={20} />}
              </button>

              {isBrowseOpen && (
                <div
                  className="fixed left-0 right-0 bg-white shadow-xl border-t border-gray-100 z-50 px-6 py-6"
                  style={{ top: '80px' }}
                >
                  <div className="max-w-7xl mx-auto">
                    <div className="mb-4 pb-3 border-b border-gray-100">
                      <Link
                        to="/jobs"
                        onClick={() => setIsBrowseOpen(false)}
                        className="inline-flex items-center gap-2 bg-blue-950 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
                      >
                        View All Jobs →
                      </Link>
                    </div>
                    <div className="grid grid-cols-6 gap-5">
                      {/* Jamaica Locations */}
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Jamaica</p>
                        <ul className="space-y-1.5">
                          {[
                            { slug: 'kingston',        label: 'Kingston' },
                            { slug: 'saint-andrew',    label: 'Saint Andrew' },
                            { slug: 'saint-catherine', label: 'Saint Catherine' },
                            { slug: 'clarendon',       label: 'Clarendon' },
                            { slug: 'manchester',      label: 'Manchester' },
                            { slug: 'saint-elizabeth', label: 'Saint Elizabeth' },
                            { slug: 'westmoreland',    label: 'Westmoreland' },
                            { slug: 'hanover',         label: 'Hanover' },
                            { slug: 'saint-james',     label: 'Saint James' },
                            { slug: 'trelawny',        label: 'Trelawny' },
                            { slug: 'saint-ann',       label: 'Saint Ann' },
                            { slug: 'saint-mary',      label: 'Saint Mary' },
                            { slug: 'portland',        label: 'Portland' },
                            { slug: 'saint-thomas',    label: 'Saint Thomas' },
                          ].map(p => (
                            <li key={p.slug}>
                              <Link
                                to={`/jobs/location/${p.slug}`}
                                onClick={() => setIsBrowseOpen(false)}
                                className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block"
                              >
                                {p.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Caribbean */}
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Caribbean</p>
                        <ul className="space-y-1.5">
                          {[
                            { slug: 'barbados',           label: 'Barbados' },
                            { slug: 'trinidad',           label: 'Trinidad & Tobago' },
                            { slug: 'guyana',             label: 'Guyana' },
                            { slug: 'bahamas',            label: 'The Bahamas' },
                            { slug: 'saint-lucia',        label: 'Saint Lucia' },
                            { slug: 'grenada',            label: 'Grenada' },
                            { slug: 'belize',             label: 'Belize' },
                            { slug: 'dominican-republic', label: 'Dominican Republic' },
                            { slug: 'cayman-islands',     label: 'Cayman Islands' },
                            { slug: 'bermuda',            label: 'Bermuda' },
                            { slug: 'haiti',              label: 'Haiti' },
                            { slug: 'saint-vincent',      label: 'Saint Vincent' },
                            { slug: 'antigua',            label: 'Antigua & Barbuda' },
                            { slug: 'turks-caicos',       label: 'Turks & Caicos' },
                          ].map(c => (
                            <li key={c.slug}>
                              <Link
                                to={`/jobs/country/${c.slug}`}
                                onClick={() => setIsBrowseOpen(false)}
                                className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block"
                              >
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Latin America */}
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Latin America</p>
                        <ul className="space-y-1.5">
                          {[
                            { slug: 'colombia',    label: 'Colombia' },
                            { slug: 'mexico',      label: 'Mexico' },
                            { slug: 'costa-rica',  label: 'Costa Rica' },
                            { slug: 'panama',      label: 'Panama' },
                            { slug: 'guatemala',   label: 'Guatemala' },
                            { slug: 'honduras',    label: 'Honduras' },
                            { slug: 'el-salvador', label: 'El Salvador' },
                            { slug: 'nicaragua',   label: 'Nicaragua' },
                            { slug: 'peru',        label: 'Peru' },
                            { slug: 'brazil',      label: 'Brazil' },
                            { slug: 'argentina',   label: 'Argentina' },
                            { slug: 'chile',       label: 'Chile' },
                            { slug: 'venezuela',   label: 'Venezuela' },
                            { slug: 'ecuador',     label: 'Ecuador' },
                          ].map(c => (
                            <li key={c.slug}>
                              <Link
                                to={`/jobs/country/${c.slug}`}
                                onClick={() => setIsBrowseOpen(false)}
                                className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block"
                              >
                                {c.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* By Industry */}
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">By Industry</p>
                        <ul className="space-y-1.5">
                          {[
                            { slug: 'technology',        label: 'Technology' },
                            { slug: 'retail',            label: 'Retail' },
                            { slug: 'healthcare',        label: 'Healthcare' },
                            { slug: 'tourism',           label: 'Tourism & Hospitality' },
                            { slug: 'food-restaurant',   label: 'Food & Restaurant' },
                            { slug: 'banking',           label: 'Banking & Finance' },
                            { slug: 'bpo',               label: 'BPO & Customer Service' },
                            { slug: 'education',         label: 'Education' },
                            { slug: 'engineering',       label: 'Engineering' },
                            { slug: 'marketing',         label: 'Marketing' },
                            { slug: 'construction',      label: 'Construction' },
                          ].map(i => (
                            <li key={i.slug}>
                              <Link
                                to={`/jobs/industry/${i.slug}`}
                                onClick={() => setIsBrowseOpen(false)}
                                className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block"
                              >
                                {i.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* By Specialty — spans 2 columns with tech + trades side by side */}
                      <div className="col-span-2">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">By Specialty</p>
                        <div className="grid grid-cols-2 gap-x-6">
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Tech</p>
                          <ul className="space-y-1.5">
                            {[
                              { slug: 'artificial-intelligence', label: 'AI & Machine Learning' },
                              { slug: 'cloud',                   label: 'Cloud Computing' },
                              { slug: 'web-development',         label: 'Web Development' },
                              { slug: 'digital-marketing',       label: 'Digital Marketing' },
                              { slug: 'ui-ux',                   label: 'UI / UX' },
                              { slug: 'blockchain',              label: 'Blockchain' },
                              { slug: 'mobile-tablet',           label: 'Mobile & Tablet' },
                              { slug: 'business-intelligence',   label: 'BI & Analytics' },
                              { slug: 'pos-payment',             label: 'POS / Payment' },
                            ].map(s => (
                              <li key={s.slug}>
                                <Link to={`/jobs/specialty/${s.slug}`} onClick={() => setIsBrowseOpen(false)} className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block">{s.label}</Link>
                              </li>
                            ))}
                          </ul>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Trade</p>
                          <ul className="space-y-1.5">
                            {[
                              { slug: 'cashier',         label: 'Cashier' },
                              { slug: 'auto-mechanic',   label: 'Auto Mechanic' },
                              { slug: 'electrician',     label: 'Electrician' },
                              { slug: 'plumber',         label: 'Plumber' },
                              { slug: 'chef-cook',       label: 'Chef / Cook' },
                              { slug: 'security-guard',  label: 'Security Guard' },
                              { slug: 'driver-delivery', label: 'Driver / Delivery' },
                              { slug: 'hvac',            label: 'HVAC Technician' },
                              { slug: 'teacher',         label: 'Teacher' },
                            ].map(s => (
                              <li key={s.slug}>
                                <Link to={`/jobs/specialty/${s.slug}`} onClick={() => setIsBrowseOpen(false)} className="text-sm text-gray-600 hover:text-blue-950 hover:font-medium transition-colors block">{s.label}</Link>
                              </li>
                            ))}
                          </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Career Resources dropdown */}
            <div className="relative" ref={resourcesDropdownRef}>
              <button
                onClick={() => setIsResourcesOpen(prev => !prev)}
                className="text-gray-700 hover:text-[#cddd3a] transition-colors flex items-center gap-1"
              >
                Career Resources
                {isResourcesOpen ? <IoMdArrowDropup size={20} /> : <IoMdArrowDropdown size={20} />}
              </button>

              {isResourcesOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 z-50 py-2">
                  {[
                    { slug: 'blog',             label: 'Career Advice Blog' },
                    { slug: 'resume-templates', label: 'Resume Templates' },
                    { slug: 'interview-tips',   label: 'Interview Tips' },
                    { slug: 'salary-guide',     label: 'Salary Guide' },
                    { slug: 'industry-reports', label: 'Industry Reports' },
                    { slug: 'get-certified',    label: 'Get Certified' },
                  ].map(item => (
                    <Link
                      key={item.slug}
                      to={`/career-resources/${item.slug}`}
                      onClick={() => setIsResourcesOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-950 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleAllCandidatesClick}
              className="text-gray-700 hover:text-[#cddd3a] transition-colors"
            >
              All Candidates
            </button>
            {shouldShowPricing() && (
              <Link to="/pricing" className="text-gray-700 hover:text-[#cddd3a] transition-colors">
                Pricing
              </Link>
            )}
            <Link to="/about-us" className="text-gray-700 hover:text-[#cddd3a] transition-colors">
              About Us
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-[#cddd3a] transition-colors">
              Contact Us
            </Link>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <AuthButtons />
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} pb-4`}>
          <div className="flex flex-col space-y-3">
            {/* Mobile Browse Jobs accordion */}
            <div className="text-center">
              <button
                onClick={() => setIsMobileBrowseOpen(prev => !prev)}
                className="w-full flex justify-center items-center py-2 text-gray-700 hover:text-[#cddd3a] transition-colors gap-1"
              >
                Browse Jobs
                {isMobileBrowseOpen ? <IoMdArrowDropup size={20} /> : <IoMdArrowDropdown size={20} />}
              </button>
              {isMobileBrowseOpen && (
                <div className="bg-gray-50 rounded-md py-3 px-4 text-left space-y-4 mt-1">
                  <Link
                    to="/jobs"
                    onClick={toggleMenu}
                    className="block text-center text-sm font-semibold bg-blue-950 text-white px-4 py-2.5 rounded-md hover:bg-[#cddd3a] hover:text-blue-950 transition-colors"
                  >
                    View All Jobs →
                  </Link>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Jamaica</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { slug: 'kingston',        label: 'Kingston' },
                        { slug: 'saint-andrew',    label: 'Saint Andrew' },
                        { slug: 'saint-catherine', label: 'Saint Catherine' },
                        { slug: 'clarendon',       label: 'Clarendon' },
                        { slug: 'manchester',      label: 'Manchester' },
                        { slug: 'saint-elizabeth', label: 'Saint Elizabeth' },
                        { slug: 'westmoreland',    label: 'Westmoreland' },
                        { slug: 'hanover',         label: 'Hanover' },
                        { slug: 'saint-james',     label: 'Saint James' },
                        { slug: 'trelawny',        label: 'Trelawny' },
                        { slug: 'saint-ann',       label: 'Saint Ann' },
                        { slug: 'saint-mary',      label: 'Saint Mary' },
                        { slug: 'portland',        label: 'Portland' },
                        { slug: 'saint-thomas',    label: 'Saint Thomas' },
                      ].map(p => (
                        <Link
                          key={p.slug}
                          to={`/jobs/location/${p.slug}`}
                          onClick={toggleMenu}
                          className="text-sm text-gray-600 hover:text-blue-950 py-0.5"
                        >
                          {p.label}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Caribbean</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { slug: 'barbados',           label: 'Barbados' },
                        { slug: 'trinidad',           label: 'Trinidad & Tobago' },
                        { slug: 'guyana',             label: 'Guyana' },
                        { slug: 'bahamas',            label: 'The Bahamas' },
                        { slug: 'saint-lucia',        label: 'Saint Lucia' },
                        { slug: 'grenada',            label: 'Grenada' },
                        { slug: 'dominican-republic', label: 'Dominican Rep.' },
                        { slug: 'cayman-islands',     label: 'Cayman Islands' },
                      ].map(c => (
                        <Link
                          key={c.slug}
                          to={`/jobs/country/${c.slug}`}
                          onClick={toggleMenu}
                          className="text-sm text-gray-600 hover:text-blue-950 py-0.5"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Latin America</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { slug: 'colombia',   label: 'Colombia' },
                        { slug: 'mexico',     label: 'Mexico' },
                        { slug: 'costa-rica', label: 'Costa Rica' },
                        { slug: 'panama',     label: 'Panama' },
                        { slug: 'guatemala',  label: 'Guatemala' },
                        { slug: 'peru',       label: 'Peru' },
                        { slug: 'brazil',     label: 'Brazil' },
                        { slug: 'argentina',  label: 'Argentina' },
                      ].map(c => (
                        <Link
                          key={c.slug}
                          to={`/jobs/country/${c.slug}`}
                          onClick={toggleMenu}
                          className="text-sm text-gray-600 hover:text-blue-950 py-0.5"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">By Industry</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { slug: 'technology',      label: 'Technology' },
                        { slug: 'retail',          label: 'Retail' },
                        { slug: 'healthcare',      label: 'Healthcare' },
                        { slug: 'tourism',         label: 'Tourism' },
                        { slug: 'food-restaurant', label: 'Food & Restaurant' },
                        { slug: 'banking',         label: 'Banking' },
                        { slug: 'bpo',             label: 'BPO' },
                        { slug: 'education',       label: 'Education' },
                        { slug: 'engineering',     label: 'Engineering' },
                      ].map(i => (
                        <Link
                          key={i.slug}
                          to={`/jobs/industry/${i.slug}`}
                          onClick={toggleMenu}
                          className="text-sm text-gray-600 hover:text-blue-950 py-0.5"
                        >
                          {i.label}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">By Specialty</p>
                    <div className="grid grid-cols-2 gap-1">
                      {[
                        { slug: 'artificial-intelligence', label: 'AI' },
                        { slug: 'cloud',                   label: 'Cloud' },
                        { slug: 'web-development',         label: 'Web Dev' },
                        { slug: 'digital-marketing',       label: 'Digital Marketing' },
                        { slug: 'ui-ux',                   label: 'UI / UX' },
                        { slug: 'cashier',                 label: 'Cashier' },
                        { slug: 'auto-mechanic',           label: 'Auto Mechanic' },
                        { slug: 'electrician',             label: 'Electrician' },
                        { slug: 'chef-cook',               label: 'Chef / Cook' },
                        { slug: 'security-guard',          label: 'Security Guard' },
                        { slug: 'driver-delivery',         label: 'Driver / Delivery' },
                        { slug: 'teacher',                 label: 'Teacher' },
                      ].map(s => (
                        <Link
                          key={s.slug}
                          to={`/jobs/specialty/${s.slug}`}
                          onClick={toggleMenu}
                          className="text-sm text-gray-600 hover:text-blue-950 py-0.5"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Mobile Career Resources accordion */}
            <div className="text-center">
              <button
                onClick={() => setIsMobileResourcesOpen(prev => !prev)}
                className="w-full flex justify-center items-center py-2 text-gray-700 hover:text-[#cddd3a] transition-colors gap-1"
              >
                Career Resources
                {isMobileResourcesOpen ? <IoMdArrowDropup size={20} /> : <IoMdArrowDropdown size={20} />}
              </button>
              {isMobileResourcesOpen && (
                <div className="bg-gray-50 rounded-md py-3 px-4 text-left mt-1 space-y-1">
                  {[
                    { slug: 'blog',             label: 'Career Advice Blog' },
                    { slug: 'resume-templates', label: 'Resume Templates' },
                    { slug: 'interview-tips',   label: 'Interview Tips' },
                    { slug: 'salary-guide',     label: 'Salary Guide' },
                    { slug: 'industry-reports', label: 'Industry Reports' },
                    { slug: 'get-certified',    label: 'Get Certified' },
                  ].map(item => (
                    <Link
                      key={item.slug}
                      to={`/career-resources/${item.slug}`}
                      onClick={toggleMenu}
                      className="block py-1.5 text-sm text-gray-600 hover:text-blue-950 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={(e) => {
                handleAllCandidatesClick(e);
                toggleMenu();
              }}
              className="text-gray-700 hover:text-[#cddd3a] transition-colors py-2 text-center w-full"
            >
              All Candidates
            </button>
            {shouldShowPricing() && (
              <Link to="/pricing" 
                    className="text-gray-700 hover:text-[#cddd3a] transition-colors py-2 text-center"
                    onClick={toggleMenu}>
                Pricing
              </Link>
            )}
            <Link to="/about-us" 
                  className="text-gray-700 hover:text-[#cddd3a] transition-colors py-2 text-center"
                  onClick={toggleMenu}>
              About Us
            </Link>
            <Link to="/contact" 
                  className="text-gray-700 hover:text-[#cddd3a] transition-colors py-2 text-center"
                  onClick={toggleMenu}>
              Contact Us
            </Link>
            
            {/* Mobile Auth Section */}
            <div className="pt-4 border-t border-gray-200">
              <MobileAuthButtons />
            </div>

          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;